import React, { useState } from 'react';
import { useNavigate, Link, useLocation, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FaUserShield, FaLock, FaEnvelope, FaArrowRight } from 'react-icons/fa';
import { ArrowLeft } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../services/api';
import { formatApiError } from '../utils/errorUtils';
import logo from '../assets/logo.png';
import './AuthPages.css';

function TeacherLoginPage() {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showForgotPassword, setShowForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetLoading, setResetLoading] = useState(false);
  const [resetMessage, setResetMessage] = useState('');
  const { login } = useAuth();
  const navigate = useNavigate();
  const approvalState = searchParams.get('approval');
  const approvalMessage = location.state?.pendingMessage
    || (approvalState === 'pending' ? 'Admin registration submitted. Approval email has been sent to epsilonskatingclub@gmail.com.' : '')
    || (approvalState === 'approved' ? 'Admin approval completed. You can now log in.' : '')
    || (approvalState === 'expired' ? 'The admin approval link has expired or was already used.' : '')
    || (approvalState === 'invalid' ? 'The admin approval link is invalid.' : '')
    || (approvalState === 'error' ? 'Admin approval could not be completed.' : '')
    || (approvalState === 'missing' ? 'Admin approval token is missing.' : '');

  const handleBack = () => {
    navigate('/');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await login(email, password);
      navigate(res.redirectPath || (res.determinedRole === 'admin' ? '/admin/dashboard' : '/dashboard'));
    } catch (err) {
      setError(err.message || 'Access Denied. Invalid admin credentials.');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setError('');
    setResetMessage('');
    setResetLoading(true);

    try {
      const response = await authAPI.requestPasswordReset({
        email: resetEmail || email,
        accountType: 'admin'
      });
      setResetMessage(response.data?.message || 'If that email exists, a password reset link has been sent.');
    } catch (err) {
      setError(formatApiError(err, 'Failed to send reset email'));
    } finally {
      setResetLoading(false);
    }
  };

  return (
    <div className="auth-shell admin-auth-wrapper">
      <div className="blur-circle circle-1" />
      <div className="blur-circle circle-2" />

      <motion.div
        className="auth-card-modern teacher-theme"
        initial={{ opacity: 0, y: 30 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.6 }}
      >
        <Link
          to="/"
          className="top-right-back-btn"
          title="Go to Home"
          aria-label="Go to Home"
        >
          <ArrowLeft size={16} strokeWidth={2.5} />
          <span>Back</span>
        </Link>

        <div className="auth-header-admin">
          <div className="auth-brand-block">
            <img src={logo} alt="Epsilon logo" className="auth-brand-logo" />
            <div>
              <h2>EPSILON <span>SKATING</span></h2>
            </div>
          </div>
        </div>

        {error && (
          <motion.div
            className="error-toast"
            initial={{ scale: 0.9 }}
            animate={{ scale: 1 }}
          >
            {error}
          </motion.div>
        )}
        {(resetMessage || approvalMessage) && <div className="success-toast">{resetMessage || approvalMessage}</div>}

        <form onSubmit={handleSubmit} className="modern-form">
          <div className="input-container">
            <label><FaEnvelope /> Email Address</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@epsilonskating.com"
              required
              disabled={loading}
            />
          </div>

          <div className="input-container">
            <label><FaLock /> Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="Enter your password"
              required
              disabled={loading}
            />
          </div>

          <button type="submit" className="btn-admin-login" disabled={loading}>
            {loading ? (
              <span className="loader-dots">Authenticating...</span>
            ) : (
              <>Login <FaArrowRight /></>
            )}
          </button>
        </form>

        <button type="button" className="forgot-toggle" onClick={() => setShowForgotPassword((prev) => !prev)}>
          {showForgotPassword ? 'Hide forgot password' : 'Forgot password?'}
        </button>

        {showForgotPassword && (
          <form onSubmit={handleForgotPassword} className="forgot-panel">
            <div className="input-container">
              <label><FaEnvelope /> Reset Email</label>
              <input
                type="email"
                value={resetEmail}
                onChange={(e) => setResetEmail(e.target.value)}
                placeholder="Enter your admin email"
                required
                disabled={resetLoading}
              />
            </div>
            <button type="submit" className="btn-inactive forgot-submit" disabled={resetLoading}>
              {resetLoading ? 'Sending...' : 'Send Reset Link'}
            </button>
          </form>
        )}

        <div className="auth-footer-links">
          <p>Not an admin? <Link to="/login">User Login</Link></p>
          <Link to="/register" className="teacher-access">Create Account</Link>
          {/* <p style={{ marginTop: '8px', fontSize: '0.82rem' }}>
            Looking for Admin Access? <Link to="/admin/register">Admin Registration</Link>
          </p> */}
        </div>
      </motion.div>
    </div>
  );
}

export default TeacherLoginPage;
